import React from 'react';
import ReactDOM from 'react-dom/client';
import { BrowserRouter } from 'react-router-dom';
import App from './App';
import { AuthProvider } from './lib/AuthContext';
import { installErrorCapture } from './lib/errorCapture';
import { BUILD_ID, RELEASE, releaseLabel } from './lib/release';
import './styles/theme.css';
import './styles/tokens.css'; // design-system tokens — must load after theme.css so its values win

// "Which build am I on?" — answerable from devtools (`document.documentElement
// .dataset.release`), the console line below, or the Admin page footer. The
// value is the short commit SHA the pipeline baked in (lib/release.ts).
document.documentElement.dataset.release = RELEASE;
if (BUILD_ID) document.documentElement.dataset.build = BUILD_ID;
console.info(`[vtt] release ${releaseLabel()}`);

// Wire frontend errors → POST /errors → ErrorsTable → vtt-errors MCP server.
// VITE_API_URL is the same HTTP API the rest of the SPA uses; the /errors
// route is intentionally unauthenticated so this works even on the login
// page or boot-time crashes before AuthProvider has a session. Every payload
// carries `release` so error_stats.by_release can answer "did the fix land?".
const apiUrl = import.meta.env.VITE_API_URL as string | undefined;
if (apiUrl) {
  installErrorCapture({
    endpoint: `${apiUrl.replace(/\/$/, '')}/errors`,
    release: RELEASE,
    build: BUILD_ID,
    source: 'frontend',
  });
}

// Apply the design-system theme + mode before first paint (see styles/tokens.css).
// Theme defaults to Obsidian Ember. Mode: an explicit choice (light/dark) wins,
// "system" follows the OS, and NO stored choice defaults to DARK — dark is the
// table's canon look, and the old follow-the-OS default dropped light-OS players
// straight into the flash-bang (session 2026-07-06). System is still available
// as an explicit opt-in via the Appearance menu.
const themeRoot = document.documentElement;
themeRoot.setAttribute('data-theme', localStorage.getItem('gd:theme') ?? 'obsidian');
const storedMode = localStorage.getItem('gd:mode');
themeRoot.setAttribute(
  'data-mode',
  storedMode === 'light' || storedMode === 'dark'
    ? storedMode
    : storedMode === 'system'
      ? (window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light')
      : 'dark',
);

ReactDOM.createRoot(document.getElementById('root')!).render(
  <React.StrictMode>
    <BrowserRouter>
      <AuthProvider>
        <App />
      </AuthProvider>
    </BrowserRouter>
  </React.StrictMode>,
);
